import { ChevronRightIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import {
  DynamicPage,
  Form,
  HeaderParamMapping,
  NewEventIntegration,
  NewLeadIntegration,
  NewPartialLeadIntegration,
  ObjectParamMapping,
  Quiz,
  WebhookIntegration,
} from '@superfunnel/types';
import Autocomplete from '@superfunnel/ui-core/Autocomplete';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@superfunnel/ui-core/v2/Collapsible';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  formFieldUrlValidator,
  isValidUrlWithParams,
} from '@superfunnel/utils';
import { diff } from 'deep-diff';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuditLog } from '../contexts/AuditLogContext';
import { useDashboardContext } from '../contexts/DashboardContext';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import {
  createWebhookIntegration,
  updateIntegration,
} from '../services/IntegrationsService';
import { classNames, distinct } from '../utils';

interface Props {
  integrationId?: string;
  integration?: WebhookIntegration;

  open: boolean;
  onClose: () => void;
  onSaved?: () => void;

  forceDynamicPagesConnection?: string[];
}

type WebhookIntegrationData = Pick<
  WebhookIntegration,
  | 'name'
  | 'url'
  | 'authentication'
  | 'extraHeaders'
  | 'objectMapping'
  | 'status'
  | 'type'
  | 'dynamicPageIds'
  | 'formIds'
  | 'contentType'
> &
  Partial<NewEventIntegration | NewLeadIntegration | NewPartialLeadIntegration>;

const defaultData = (): Partial<WebhookIntegrationData> => {
  return {
    type: 'new_lead',
  };
};

export const EditWebhookIntegrationModal = ({
  open,
  onClose,
  onSaved,
  integrationId,
  integration,
  forceDynamicPagesConnection,
}: Props) => {
  const { show } = useSnackbarContext();
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages, forms, quizzes } = useDashboardContext();
  const auditLog = useAuditLog();

  const [data, setData] = useState<Partial<WebhookIntegrationData>>(
    integration ?? defaultData(),
  );

  const [eventTypeQuery, setEventTypeQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const copy: Partial<WebhookIntegrationData> = integration
      ? JSON.parse(JSON.stringify(integration))
      : undefined;
    setData(
      copy
        ? {
            name: copy.name,
            url: copy.url,
            authentication: copy.authentication,
            extraHeaders: copy.extraHeaders,
            objectMapping: copy.objectMapping,
            status: copy.status,
            type: copy.type,
            dynamicPageIds: copy.dynamicPageIds,
            formIds: copy.formIds,
            eventTypes: 'eventTypes' in copy ? copy.eventTypes : undefined,
            contentType: copy.contentType ?? 'json',
          }
        : defaultData,
    );
  }, [integration]);

  const onSave = useCallback(() => {
    if (!selectedAccount) {
      return;
    }

    if (!data.url) {
      show('URL is required', 'warn');
      return;
    }

    if (!isValidUrlWithParams(data.url)) {
      show('Invalid URL', 'warn');
      return;
    }

    if (data.type === 'new_event' && !data.eventTypes?.length) {
      show('Event Types are required', 'warn');
      return;
    }

    setSaving(true);

    let promise: Promise<any>;
    if (integrationId) {
      promise = updateIntegration(selectedAccount.id, integrationId, data);
    } else {
      if (!data.url) {
        return;
      }
      promise = createWebhookIntegration(
        selectedAccount.id,
        data.type ?? 'new_lead',
        data.name ??
          (data.type === 'new_lead' ? 'New Lead Webhook' : 'Event Webhook'),
        data.url,
        data.objectMapping || [],
        data.extraHeaders || [],
        forceDynamicPagesConnection
          ? forceDynamicPagesConnection
          : data.dynamicPageIds || [],
        data.type !== 'new_event' ? data.formIds || [] : [],
        data.authentication,
        'eventTypes' in data ? data.eventTypes : undefined,
      );
    }

    promise
      .then(() => {
        onClose();
        onSaved?.();
        show(
          integrationId
            ? 'Webhook integration updated'
            : 'Webhook integration created',
          'success',
        );

        auditLog.log(
          integrationId ? 'update' : 'create',
          integrationId
            ? 'Update webhook integration'
            : 'Create webhook integration',
          'integration',
          integrationId,
          data.name,
          integration
            ? diff(integration, { ...integration, ...data })
            : undefined,
        );
      })
      .catch((e) => {
        show('Failed to save webhook integration', 'warn');
        console.error(e);
        Sentry.captureException(e);
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    auditLog,
    data,
    forceDynamicPagesConnection,
    integration,
    integrationId,
    onClose,
    onSaved,
    selectedAccount,
    show,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: integration ? 'Update' : 'Create',
        type: 'primary',
        loading: saving,
        onClick: () => onSave(),
      },
    ],
    [saving, integration, onClose, onSave],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="3xl"
      title={integration ? 'Update Webhook' : 'Create Webhook'}
    >
      <div className="mt-0 flex flex-col gap-4">
        <Input
          title="Name"
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          placeholder='e.g. "New Lead Webhook"'
        />

        <div className="flex flex-col gap-1">
          <FieldTitle title="Trigger" />
          <Select
            disabled={!!integration}
            value={data.type ?? 'new_lead'}
            onValueChange={(value: WebhookIntegration['type']) =>
              setData({ ...data, type: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new_event">Event</SelectItem>
              <SelectItem value="new_lead">Lead</SelectItem>
              <SelectItem value="new_partial_lead">Partial Lead</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {data.type === 'new_event' && (
          <ChipsAutocomplete<NonNullable<NewEventIntegration['eventTypes']>[0]>
            modal
            title="Event Types"
            selected={data.eventTypes ?? []}
            onChange={(value) => setData({ ...data, eventTypes: value })}
            getValue={(a) => (a === '*' ? 'All' : a)}
            onInputChange={(value) => setEventTypeQuery(value)}
            options={distinct(
              [
                '*',
                'view',
                'conversion',
                'button-click',
                eventTypeQuery,
              ].filter((x) => !!x),
            )}
          />
        )}

        <Input
          title="URL"
          value={data.url}
          onChange={(e) => setData({ ...data, url: e.target.value })}
          validation={formFieldUrlValidator(data.url ?? '', true)}
          placeholder='e.g. "https://example.com/webhook"'
        />

        {!!integration && (
          <div>
            <label className="block pb-1 text-sm leading-6 font-medium text-gray-900">
              Status
            </label>
            <div className="flex items-center space-x-3">
              <Switch
                checked={data.status === 'active'}
                onCheckedChange={(value) =>
                  setData({ ...data, status: value ? 'active' : 'disabled' })
                }
              />
              <Label className="text-sm font-medium">
                {data.status === 'active' ? 'Active' : 'Disabled'}
              </Label>
            </div>
          </div>
        )}

        {(forceDynamicPagesConnection === undefined || !!integration) && (
          <ChipsAutocomplete<QueryDocumentSnapshot<DynamicPage, DynamicPage>>
            modal
            title="Limit to specific Dynamic Pages"
            selected={
              data.dynamicPageIds
                ?.map((id) => dynamicPages?.find((p) => p.id === id))
                .filter((x) => !!x) ?? []
            }
            onChange={(value) =>
              setData({
                ...data,
                dynamicPageIds: value.length
                  ? value?.map((v) => v?.id)
                  : undefined,
              })
            }
            getOptionId={(a) => a.id}
            getValue={(a) => a.data().name}
            loading={dynamicPages === undefined}
            options={dynamicPages ?? []}
            disabled={forceDynamicPagesConnection !== undefined}
          />
        )}

        {data.type !== 'new_event' && (
          <ChipsAutocomplete<
            | QueryDocumentSnapshot<Form, Form>
            | QueryDocumentSnapshot<Quiz, Quiz>
          >
            modal
            title="Limit to specific Forms / Quizzes"
            selected={
              data.formIds
                ?.map(
                  (id) =>
                    forms?.find((f) => f.id === id) ??
                    quizzes?.find((q) => q.id === id),
                )
                .filter((x) => !!x) ?? []
            }
            onChange={(value) =>
              setData({
                ...data,
                formIds: value.length ? value?.map((v) => v?.id) : undefined,
              })
            }
            getOptionId={(a) => a.id}
            getValue={(a) => a.data().name}
            loading={forms === undefined || quizzes === undefined}
            options={[...(forms ?? []), ...(quizzes ?? [])]}
          />
        )}
        <Collapsible className="group">
          <CollapsibleTrigger
            className={classNames(
              'flex w-full items-center gap-1 py-2',
              'group-data-[state=open]:border-b group-data-[state=open]:border-gray-200',
            )}
          >
            <span className="block text-sm leading-6 font-medium text-gray-900">
              Advanced Settings
            </span>
            <div className="flex-1" />
            <ChevronRightIcon className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className="flex flex-col gap-4">
              <div>
                <div className="flex justify-between">
                  <h4 className="text-sm leading-5 font-semibold text-gray-900">
                    Object Mapping
                  </h4>
                </div>

                <ObjectMappingForm
                  type={data.type || 'new_lead'}
                  objectMapping={data.objectMapping ?? []}
                  onChange={(mapping) =>
                    setData({ ...data, objectMapping: mapping })
                  }
                />
              </div>

              <Separator />

              <div>
                <div className="flex flex-col gap-1">
                  <FieldTitle title="Authentication Type" />
                  <Select
                    value={data.authentication?.type ?? 'jwt'}
                    onValueChange={(value) =>
                      setData({
                        ...data,
                        authentication:
                          value === 'apiKey'
                            ? {
                                type: value as 'apiKey',
                                headerName: 'Authorization',
                                value: '',
                              }
                            : undefined,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apiKey">API Key</SelectItem>
                      <SelectItem value="jwt">Public/Private Key</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {data.authentication?.type === 'apiKey' && (
                  <>
                    <Input
                      title="Header Name"
                      value={data.authentication.headerName}
                      onChange={(e) =>
                        setData({
                          ...data,
                          authentication: {
                            value: data.authentication?.value ?? '',
                            type: 'apiKey',
                            headerName: e.target.value,
                          },
                        })
                      }
                    />
                    <Input
                      title="API Key"
                      value={data.authentication.value}
                      onChange={(e) =>
                        setData({
                          ...data,
                          authentication: {
                            value: e.target.value,
                            type: 'apiKey',
                            headerName: data.authentication?.headerName ?? '',
                          },
                        })
                      }
                    />
                  </>
                )}
              </div>

              <Separator />

              <div>
                <div className="flex flex-col gap-1">
                  <FieldTitle title="Content Type" />
                  <Select
                    value={data.contentType ?? 'json'}
                    onValueChange={(value) =>
                      setData({
                        ...data,
                        contentType: value as
                          | 'json'
                          | 'form-data'
                          | 'urlencoded',
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="form-data">Form Data</SelectItem>
                      <SelectItem value="urlencoded">URL Encoded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div>
                <FieldTitle title="Extra Headers" />
                <div>
                  <ObjectMappingForm
                    type={data.type || 'new_lead'}
                    objectMapping={data.extraHeaders ?? []}
                    onChange={(mapping) =>
                      setData({
                        ...data,
                        extraHeaders: mapping as HeaderParamMapping[],
                      })
                    }
                    allowedTypes={['custom', 'param']}
                  />
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </Modal>
  );
};

const ObjectMappingForm = ({
  type,
  objectMapping,
  onChange,
  allowedTypes = ['custom', 'param', 'map'],
}: {
  type: 'new_lead' | 'new_event' | 'new_partial_lead';
  objectMapping: ObjectParamMapping[];
  onChange: (mapping: ObjectParamMapping[]) => void;
  allowedTypes?: ObjectParamMapping['value']['type'][];
}) => {
  const [parameterPathQuery, setParameterPathQuery] = useState('');
  const parameterPathOptions = useMemo(() => {
    return type === 'new_lead' || type === 'new_partial_lead'
      ? leadKeys
      : eventKeys;
  }, [type]);

  return (
    <div className="pt-2">
      {objectMapping.map((item, index) => (
        <div
          className="my-2 flex w-full flex-col gap-2 rounded-lg border border-gray-200 p-2"
          // eslint-disable-next-line react/no-array-index-key
          key={index}
        >
          <div className="flex w-full items-center gap-4">
            <Input
              className="w-full"
              placeholder="Key"
              value={item.key}
              onChange={(e) => {
                const key = e.target.value;
                if (objectMapping.some((m) => m.key === key)) {
                  return;
                }
                onChange(
                  objectMapping.map((m) =>
                    m.key === item.key ? { ...m, key } : m,
                  ),
                );
              }}
            />
            <div className="flex shrink-0 gap-2">
              <Select
                value={item.value.type}
                onValueChange={(value: 'custom' | 'param' | 'map') =>
                  onChange(
                    objectMapping.map((m) =>
                      m.key === item.key
                        ? {
                            ...m,
                            value: {
                              key: m.key,
                              type: value,
                              value: (value === 'map' ? [] : '') as any,
                            },
                          }
                        : m,
                    ),
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Value Type" />
                </SelectTrigger>
                <SelectContent>
                  {[
                    { value: 'custom' as const, label: 'Custom Value' },
                    { value: 'param' as const, label: 'Parameter' },
                    { value: 'map' as const, label: 'Map' },
                  ]
                    .filter((o) => allowedTypes.includes(o.value))
                    .map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                color="negative"
                onClick={() =>
                  onChange(objectMapping.filter((m) => m.key !== item.key))
                }
              >
                Remove
              </Button>
            </div>
          </div>
          {item.value.type === 'custom' && (
            <TypedTextField
              value={item.value.value}
              onChange={(value) =>
                onChange(
                  objectMapping.map((m) =>
                    m.key === item.key
                      ? {
                          ...m,
                          value: {
                            key: m.key,
                            type: 'custom',
                            value,
                          },
                        }
                      : m,
                  ),
                )
              }
            />
          )}
          {item.value.type === 'param' && (
            <>
              <Autocomplete
                modal
                placeholder="Parameter Path"
                selected={item.value.value}
                onInputChange={(value) => setParameterPathQuery(value)}
                renderOption={(option) => {
                  const item = parameterPathOptions.find(
                    (x) => x.key === option,
                  );

                  if (!item) {
                    return <>{option}</>;
                  }

                  return (
                    <>
                      <span>{item.key}</span>
                      <span className="text-sx ms-2 text-gray-600">
                        Example: {item.example}
                      </span>
                    </>
                  );
                }}
                onChange={(value) =>
                  onChange(
                    objectMapping.map((m) =>
                      m.key === item.key
                        ? {
                            ...m,
                            value: {
                              ...m.value,
                              type: 'param',
                              value: value || '',
                            },
                          }
                        : m,
                    ),
                  )
                }
                options={[
                  ...(parameterPathQuery ? [parameterPathQuery] : []),
                  ...parameterPathOptions.map((x) => x.key),
                ]}
              />
              <div className="flex flex-col gap-1">
                <FieldTitle title="Default Value" />
                <TypedTextField
                  placeholder="Default Value"
                  value={item.value.defaultValue}
                  onChange={(value) =>
                    onChange(
                      objectMapping.map((m) =>
                        m.key === item.key
                          ? {
                              ...m,
                              value: {
                                ...(m.value as any),
                                type: 'param',
                                defaultValue: value,
                              },
                            }
                          : m,
                      ),
                    )
                  }
                />
              </div>
              <Select
                value={item.value.dataType ?? 'none'}
                onValueChange={(value: 'none' | 'date') =>
                  onChange(
                    objectMapping.map((m) =>
                      m.key === item.key
                        ? {
                            ...m,
                            value: {
                              ...m.value,
                              type: 'param',
                              value: m.value.value,
                              dataType: value === 'none' ? null : 'date',
                            },
                          }
                        : m,
                    ),
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Data Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                </SelectContent>
              </Select>
              {item.value.dataType === 'date' && (
                <>
                  <Input
                    placeholder="Date Format"
                    value={item.value.dateFormat ?? ''}
                    onChange={(e) =>
                      onChange(
                        objectMapping.map((m) =>
                          m.key === item.key
                            ? {
                                ...m,
                                value: {
                                  ...m.value,
                                  dateFormat: e.target.value,
                                },
                              }
                            : m,
                        ),
                      )
                    }
                  />
                  <Input
                    placeholder="Timezone"
                    value={item.value.timezone ?? ''}
                    onChange={(e) =>
                      onChange(
                        objectMapping.map((m) =>
                          m.key === item.key
                            ? {
                                ...m,
                                value: {
                                  ...m.value,
                                  timezone: e.target.value,
                                },
                              }
                            : m,
                        ),
                      )
                    }
                  />
                </>
              )}
            </>
          )}
          {item.value.type === 'map' && (
            <div className="flex w-full gap-2">
              <div className="h-full w-1 bg-gray-300" />
              <div className="flex-1">
                <ObjectMappingForm
                  type={type}
                  objectMapping={item.value.value}
                  onChange={(value) =>
                    onChange(
                      objectMapping.map((m) =>
                        m.key === item.key
                          ? {
                              ...m,
                              value: {
                                key: m.key,
                                type: 'map',
                                value,
                              },
                            }
                          : m,
                      ),
                    )
                  }
                />
              </div>
            </div>
          )}
        </div>
      ))}
      <div className="mt-2 flex w-full">
        <Button
          variant="outline"
          className="w-full"
          onClick={() =>
            onChange([
              ...objectMapping,
              {
                key: '',
                value: {
                  type: 'custom',
                  value: '',
                },
              },
            ])
          }
        >
          Add Value
        </Button>
      </div>
    </div>
  );
};

interface TypedTextFieldProps {
  placeholder?: string;
  value: any;
  onChange: (value: any) => void;
}

const TypedTextField = ({
  placeholder,
  value,
  onChange,
}: TypedTextFieldProps) => {
  const type = useMemo(() => {
    return typeof value === 'string'
      ? 'string'
      : typeof value === 'number'
        ? 'number'
        : typeof value === 'boolean'
          ? 'boolean'
          : 'null';
  }, [value]);

  return (
    <div className="flex w-full items-center gap-4">
      <div className={type === 'null' ? 'w-full' : 'w-32'}>
        <Select
          value={type}
          onValueChange={(value: 'string' | 'number' | 'boolean' | 'null') =>
            onChange(
              value === 'string'
                ? ''
                : value === 'number'
                  ? 0
                  : value === 'boolean'
                    ? false
                    : null,
            )
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="string">String</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
            <SelectItem value="null">Null</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {type !== 'null' && (
        <div className="flex-1">
          {(type === 'string' || type === 'number') && (
            <Input
              placeholder={placeholder || 'Value'}
              value={value}
              type={type === 'number' ? 'number' : 'text'}
              onChange={(e) => {
                if (type === 'number') {
                  onChange(parseFloat(e.target.value));
                } else {
                  onChange(e.target.value);
                }
              }}
            />
          )}
          {type === 'boolean' && (
            <Select
              value={value ? 'true' : 'false'}
              onValueChange={(value: 'true' | 'false') =>
                onChange(value === 'true')
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={placeholder || 'Value'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">True</SelectItem>
                <SelectItem value="false">False</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
};

const leadKeys = [
  { key: 'type', example: 'new_lead' },
  { key: 'data.accountId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.campaignId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.variantId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.websiteId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.formId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.form.name.value', example: 'John Doe' },
  { key: 'data.form.name.label', example: 'Name' },
  { key: 'data.form.email.value', example: 'john@example.com' },
  { key: 'data.form.email.label', example: 'Email' },
  { key: 'data.form.phone.value', example: 'xxxxxxxxxxx' },
  { key: 'data.form.phone.label', example: 'Phone' },
  { key: 'data.form.message.value', example: '' },
  { key: 'data.form.message.label', example: 'Message' },
  { key: 'data.url', example: 'https://example.page/xxxxxxxxxxx' },
  { key: 'data.userHash', example: 1000000000000000 },
  { key: 'data.sessionHash', example: 1000000000000000 },
  { key: 'data.id', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.queryParams.gclid', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.formName', example: 'Lead capture form' },
];

const eventKeys = [
  { key: 'type', example: 'new_event' },
  { key: 'data._id', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.createdOn', example: '2025-02-10T15:00:09.812Z' },
  { key: 'data.accountId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.campaignId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.variantId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.websiteId', example: 'xxxxxxxxxxxxxxxxxxxx' },
  { key: 'data.event', example: 'ping' },
  { key: 'data.path', example: '/xxxxxxxx' },
  { key: 'data.search', example: '?gclid=xxxxxx' },
  { key: 'data.title', example: 'Home' },
  { key: 'data.hash', example: 1000000000000000 },
  { key: 'data.sessionHash', example: 1000000000000000 },
  { key: 'data.visitHash', example: 1000000000000000 },
  { key: 'data.ip', example: '1.1.1.1' },
  { key: 'data.device', example: 'Desktop' },
  { key: 'data.os', example: 'Mac OS' },
  { key: 'data.browser', example: 'Chrome' },
  {
    key: 'data.userAgent',
    example:
      'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/132.0.0.0 Safari/537.36',
  },
  { key: 'data.origin', example: 'https://test.test.test' },
  { key: 'data.referrer', example: '' },
  { key: 'data.gclid', example: null },
  { key: 'data.shortHash', example: 'xxxxxxxxx' },
  { key: 'data.shortSessionHash', example: 'xxxxxxxxx' },
  { key: 'data.shortVisitHash', example: 'xxxxxxxxx' },
  { key: 'data.createdOnUnix', example: 10000000000 },
  { key: 'data.queryParams.gclid', example: 'xxxxxxxxxxxxxxxxxxxx' },
];
